import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {convertToModels} from 'shells/filter-capsule/convert-to-models';
import {FilterCapsule} from 'shells/filter-capsule/filter-capsule';

import type {NutshellState} from '../../store';

type Props = {
    canViewAllMeetings: boolean;
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject) => void;
};

export const MEETING_TYPE_FILTERS = [
    {id: 'SOLO', name: 'Solo'},
    {id: 'COHOSTED', name: 'Co-host'},
    {id: 'ROUND_ROBIN', name: 'Round-robin'},
] as const;

export function MeetingsFilters(props: Props) {
    const assignables = useSelector((state: NutshellState) => Assignables.getAll(state));
    const _isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    return (
        <>
            {props.canViewAllMeetings ? (
                <FilterCapsule
                    key='host'
                    size='big'
                    collection={convertToModels(assignables)}
                    filter={safelyGetFilterByKey(props.activeFilters, 'host')}
                    filterName='host'
                    defaultValueTitle='Meeting host'
                    schemaField={{
                        name: 'host',
                        type: 'relationship',
                        title: 'Host',
                        description:
                            "The Nutshell user or team that 'created' the Scheduler meeting",
                        isFilterable: true,
                        isDisplayable: true,
                        isSortable: true,
                        isAndable: true,
                        relationships: ['users', 'teams'],
                        size: 'md',
                    }}
                    onSubmit={props.onUpdateFilters}
                    onRemove={() => {
                        props.onUpdateFilters({
                            host: null,
                        });
                    }}
                />
            ) : undefined}
            <FilterCapsule
                key='meetingType'
                size='big'
                // @ts-expect-error
                collection={convertToModels(MEETING_TYPE_FILTERS)}
                filter={safelyGetFilterByKey(props.activeFilters, 'meetingType')}
                filterName='meetingType'
                defaultValueTitle='Meeting type'
                schemaField={{
                    name: 'meetingType',
                    type: 'enum',
                    cellType: 'description',
                    enum: MEETING_TYPE_FILTERS.map((meetingType) => ({
                        id: meetingType.id,
                        name: meetingType.name,
                        type: 'enum',
                    })),
                    title: 'meetingType',
                    description: 'The type of meeting: Solo, Co-hosted, or Round-robin',
                    isFilterable: true,
                    isDisplayable: true,
                    isSortable: true,
                    isAndable: true,
                    relationships: ['meetingType'],
                    size: 'md',
                }}
                onSubmit={props.onUpdateFilters}
                onRemove={() => {
                    props.onUpdateFilters({
                        meetingType: null,
                    });
                }}
            />
        </>
    );
}
